import { ProjectOutlined } from "@ant-design/icons";
import {
  DocumentLibraryView,
  PageHeader,
  type DocumentLibraryProject,
  type SharePointItem
} from "@mj/shared-ui";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { sharepointApi } from "./api";
import {
  useFolderItems,
  useItemVersions,
  useResolveProjectFolder
} from "./hooks/useSharePoint";
import { uploadFile } from "./upload";

import EmptyState from "@/components/common/EmptyState";
import Layout from "@/components/common/Layout";
import { AZURE_BASE_URL } from "@/config/api";
import { CODE_TO_UK_REGIONS_MAP } from "@/constants/region";
import { useUser } from "@/contexts/UserContext";
import { useProjects } from "@/hooks/businessCentral";
import { getBffToken } from "@/utils/auth";
import storage from "@/utils/storage";

const DRAWING_COMPARE_CONFIG = {
  apiUrl: AZURE_BASE_URL,
  getAuthToken: getBffToken
};

const loadFileBlob = (itemId: string, versionId: string | null) =>
  versionId
    ? sharepointApi.getItemVersionContent(itemId, versionId)
    : sharepointApi.getItemContent(itemId);

const DocumentLibraryPage = () => {
  const user = useUser();

  const navigate = useNavigate();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [versionItemId, setVersionItemId] = useState<string | null>(null);

  const { isLoading: projectsLoading, data: projectsData } = useProjects();
  const resolveProjectFolder = useResolveProjectFolder();
  const folderItemsQ = useFolderItems(currentFolderId);
  const itemVersionsQ = useItemVersions(versionItemId);

  // BC projects filtered by the user's region — match by exact region name.
  const projects: DocumentLibraryProject[] = useMemo(() => {
    if (!projectsData?.value?.length) return [];
    return projectsData.value
      .filter((project) => {
        const regionName = CODE_TO_UK_REGIONS_MAP[project.jobPostingGroup];
        if (!regionName) return false;
        return regionName === user?.region;
      })
      .map((p) => ({
        no: p.no,
        description: p.description,
        opportunityNo: p.opportunityNo ?? undefined
      }));
  }, [projectsData, user]);

  // Saved project is stored as `${no}~~${description}` — resolve back to the
  // description (folder key) once the project list is known.
  const initialProjectKey = useMemo(() => {
    const savedNo = storage.getProject()?.split("~~")?.[0];
    if (!savedNo) return undefined;
    return projects.find((p) => p.no === savedNo)?.description;
  }, [projects]);

  // "No projects at all" is a page-level concern, not something
  // DocumentLibraryView (which MJ-Tracker also renders) should know about —
  // so it short-circuits here, keeping the page header for orientation.
  if (!projectsLoading && projects.length === 0) {
    return (
      <Layout>
        <PageHeader
          title="Document Library"
          description="Browse and manage opportunity documents from SharePoint"
        />
        <EmptyState
          icon={<ProjectOutlined />}
          title="No projects found"
          description="There are no projects available for your region yet."
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <DocumentLibraryView
        projects={projects}
        projectsLoading={projectsLoading}
        initialProjectKey={initialProjectKey}
        resolveProjectFolder={resolveProjectFolder}
        onCurrentFolderChange={(folder: SharePointItem | null) =>
          setCurrentFolderId(folder?.id ?? null)
        }
        folderItems={folderItemsQ.data}
        folderItemsLoading={folderItemsQ.isLoading}
        folderItemsError={folderItemsQ.error}
        refetchFolderItems={() => {
          folderItemsQ.refetch();
        }}
        onVersionItemChange={(item: SharePointItem | null) =>
          setVersionItemId(item?.id ?? null)
        }
        itemVersions={itemVersionsQ.data}
        itemVersionsLoading={itemVersionsQ.isLoading}
        itemVersionsError={itemVersionsQ.error}
        loadFileBlob={loadFileBlob}
        uploadFile={uploadFile}
        drawingCompare={DRAWING_COMPARE_CONFIG}
        onBackToDashboard={() => navigate("/dashboard")}
      />
    </Layout>
  );
};

export default DocumentLibraryPage;
